import React, { FC } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { defaultsDeep } from 'lodash';
import { getAnchorRowStyle, FormattedTextField } from 'framework/ui/field';
import {
  AmountSensitiveField,
  FactDateField,
  PlannedDateField,
  StatusField,
  PeriodicPeriodField,
  PeriodicActionField,
  PreviewBillingRequestLinkField,
} from '../fields';
import { EditableListDataGridLayout, EDITABLE_LIST_FIELD } from 'framework/list';

const PeriodicBillingListGrid: FC<{ isSensitiveDataIsShown?: boolean }> = ({ isSensitiveDataIsShown }) => {
  const { hash } = useLocation();
  const { id } = useParams();

  return (
    <EditableListDataGridLayout
      rerenderTableCellsDeps={[isSensitiveDataIsShown]}
      rowProps={data => {
        return defaultsDeep(getAnchorRowStyle(data, hash), {
          sx: {
            padding: 0,
            px: 2,
            alignItems: 'center',
            py: 2,
          },
        });
      }}
      getTableCells={isEditModeLayout =>
        Promise.resolve([
          {
            type: EDITABLE_LIST_FIELD,
            source: 'position',
            isEditor: false,
            component: PreviewBillingRequestLinkField,
            componentProps: {
              billingScheduleIdSource: 'billingScheduleId',
              billingRequestIdSource: 'billingRequestId',
              id,
              statusSource: 'status',
            },
            gridCellProps: { size: 0.5 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'position',
            isEditor: false,
            component: PeriodicPeriodField,
            componentProps: {
              label: '',
              periodSource: 'periodMonth',
              startDateSource: 'startDate',
              endDateSource: 'endDate',
              changeRequestAliasSource: 'changeRequestAlias',
            },
            gridCellProps: { size: 2 },
          },

          {
            type: EDITABLE_LIST_FIELD,
            source: 'sensitiveData',
            isEditor: false,
            component: AmountSensitiveField,
            componentProps: { label: '', showContent: isSensitiveDataIsShown, tooltipText: 'Planned Amount' },
            gridCellProps: { size: 1 },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'amount',
            isEditor: false,
            component: AmountSensitiveField,
            componentProps: {
              label: '',
              showContent: isSensitiveDataIsShown,
              currencySource: 'currency',
              tooltipText: 'Actual Amount',
            },
            gridCellProps: { size: 1 },
          },

          {
            type: EDITABLE_LIST_FIELD,
            source: 'hours',
            isEditor: false,
            component: FormattedTextField,
            componentProps: { label: '', format: (val: string) => (val ? `${val} h` : '—') },
            gridCellProps: { size: 0.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'invoiceDate',
            isEditor: false,
            component: PlannedDateField,
            componentProps: { label: '', tooltipText: 'Planned date' },
            gridCellProps: {
              size: 1.5,
            },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'billingRequestDate',
            isEditor: false,
            component: FactDateField,
            componentProps: {
              plannedDateSource: 'invoiceDate',
              tooltipText: 'Fact date',
              isLinkedCRSource: 'isLinkedCR',
            },
            gridCellProps: {
              size: 1.5,
            },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'status',
            isEditor: false,
            component: StatusField,
            componentProps: { id: id, sourceId: 'id', parentAliasSource: 'position' },
            gridCellProps: { size: 1.5, textAlign: 'end', sx: { '.MuiFormControl-root': { mb: 0 } } },
          },
          {
            type: EDITABLE_LIST_FIELD,
            source: 'status',
            isEditor: false,
            component: PeriodicActionField,
            componentProps: { id },
            gridCellProps: { size: 1, textAlign: 'end', sx: { '.MuiFormControl-root': { mb: 0 } } },
          },
        ])
      }
    />
  );
};

export default PeriodicBillingListGrid;
